import { buildSavedViewFiltersJson, parseSavedViewFilters } from '../../shared/saved-views/saved-view-filters';
import { TranslateService } from '@ngx-translate/core';
import { Requirement, RequirementStatusCategory, RequirementStatusOption } from '../../models/requirement.model';
import { requirementTypeSlug } from '../../shared/requirement-type-defaults';
import { RequirementTypeKind } from '../../components/type-pill/type-pill.component';
import { PriorityLevel } from '../../components/priority-pill/priority-pill.component';
import { ReqStatusKind } from '../../components/req-status-pill/req-status-pill.component';
import { ReqRow, RequirementSavedViewFilters, StatusTab, TableSortCol, TabCounts } from './requirements.types';

export const PRIORITY_RANK: Record<string, number> = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 };

// Fallback fuer Deep-Links aus dem Live-Dashboard (Enum-Codes) sowie fuer alte
// Records, deren Status noch nicht auf einen projekt-lokalen Wert migriert ist.
const LEGACY_STATUS_TO_TAB: Record<string, StatusTab> = {
  DRAFT: 'offen',
  REVIEW: 'review',
  APPROVED: 'fertig',
  IMPLEMENTED: 'fertig',
};

const LEGACY_STATUS_TO_LABEL: Record<string, string> = {
  DRAFT: 'Entwurf',
  REVIEW: 'Im Review',
  APPROVED: 'Freigegeben',
  IMPLEMENTED: 'Abgeschlossen',
  OBSOLETE: 'Veraltet',
};

// Die Tabelle kennt weniger Zustaende als das Backend: angefangene Arbeit zaehlt
// als offen, freigegeben als fertig.
const CATEGORY_TO_STATUS_CATEGORY: Record<RequirementStatusCategory, ReqRow['statusCategory']> = {
  OPEN: 'open',
  IN_PROGRESS: 'open',
  REVIEW: 'review',
  APPROVED: 'done',
  DONE: 'done',
};

const CATEGORY_TO_TAB: Record<RequirementStatusCategory, StatusTab> = {
  OPEN: 'offen',
  IN_PROGRESS: 'offen',
  REVIEW: 'review',
  APPROVED: 'fertig',
  DONE: 'fertig',
};

/**
 * Uebersetzt den Status-Deeplink-Param aus dem Live-Dashboard-Funnel (Enum-Code
 * oder projekt-lokaler Wert) in die passende Filter-Tab-Kategorie. Unbekannte
 * Werte liefern {@code null}, damit die Liste ungefiltert bleibt.
 */
export function statusTabFromParam(
  status: string | null | undefined,
  options: RequirementStatusOption[] = [],
): StatusTab {
  if (!status) return null;
  const trimmed = status.trim();
  const upper = trimmed.toUpperCase();
  const legacy = LEGACY_STATUS_TO_TAB[upper];
  if (legacy) return legacy;
  // Fallback statt undefined: eine Kategorie, die erst im Backend existiert, darf
  // die Seite nicht in einen ungueltigen Zustand bringen.
  const match = options.find((o) => o.value === trimmed);
  if (match) return CATEGORY_TO_TAB[match.category] ?? null;
  return null;
}

export function pillClass(modifier: string, value: string): string {
  const slug = value.toLowerCase().replaceAll(' ', '-').replaceAll('_', '-');
  return `req-pill req-pill--${modifier}-${slug}`;
}

/**
 * Ordnet einem Status-String eine Kategorie zu. Priorisiert das aktuelle
 * Projekt-Mapping; fällt bei Legacy-Enum-Codes (DRAFT/REVIEW/…) auf die
 * Standard-Zuordnung zurueck.
 */
function categorize(status: string, options: RequirementStatusOption[]): ReqRow['statusCategory'] {
  const match = options.find((o) => o.value === status);
  if (match) return CATEGORY_TO_STATUS_CATEGORY[match.category] ?? 'other';
  const legacyTab = LEGACY_STATUS_TO_TAB[status.toUpperCase()];
  if (legacyTab === 'offen') return 'open';
  if (legacyTab === 'review') return 'review';
  if (legacyTab === 'fertig') return 'done';
  return 'other';
}

function statusLabel(status: string): string {
  return LEGACY_STATUS_TO_LABEL[status.toUpperCase()] ?? status;
}

// Mapping der REQUIREMENT_TYPE_SLUGS (`requirement-type-defaults.ts:42-44`) auf die
// nach NEX-1086-Review erweiterte RequirementTypeKind-Union (NEX-1085). Der
// `custom`-Slug (Fallback von `requirementTypeSlug` fuer projekt-lokale Freitext-
// Typen) MUSS hier stehen — ohne ihn wuerde der `?? 'funktional'`-Fallback in
// reqToRow importierte Custom-Typen (z. B. "Epic") faelschlich blau als
// funktional einfaerben (sgraetz C1).
const TYPE_SLUG_TO_KIND: Record<string, RequirementTypeKind> = {
  functional: 'funktional',
  'non-functional': 'nicht-funktional',
  bug: 'bug',
  business: 'fachlich',
  technical: 'technisch',
  story: 'story',
  substory: 'substory',
  custom: 'custom',
};

const PRIORITY_TO_LEVEL: Record<string, PriorityLevel> = {
  LOW: 'niedrig',
  MEDIUM: 'mittel',
  HIGH: 'hoch',
  CRITICAL: 'kritisch',
};

const BE_CATEGORY_TO_KIND: Record<RequirementStatusCategory, ReqStatusKind> = {
  OPEN: 'entwurf',
  IN_PROGRESS: 'in-bearbeitung',
  REVIEW: 'im-review',
  APPROVED: 'freigegeben',
  DONE: 'abgeschlossen',
};

const LEGACY_STATUS_TO_KIND: Record<string, ReqStatusKind> = {
  DRAFT: 'entwurf',
  REVIEW: 'im-review',
  APPROVED: 'freigegeben',
  IMPLEMENTED: 'abgeschlossen',
  OBSOLETE: 'entwurf',
};

function resolveStatusKind(status: string, options: RequirementStatusOption[]): ReqStatusKind {
  const match = options.find((o) => o.value === status);
  if (match) return BE_CATEGORY_TO_KIND[match.category] ?? 'entwurf';
  return LEGACY_STATUS_TO_KIND[status.toUpperCase()] ?? 'entwurf';
}

export function reqToRow(
  r: Requirement,
  translate: TranslateService,
  statusOptions: RequirementStatusOption[] = [],
): ReqRow {
  const statusCategory = categorize(r.status, statusOptions);
  const typeSlug = requirementTypeSlug(r.type);
  return {
    id: r.id,
    displayId: r.extId ?? r.key ?? '',
    title: r.title,
    typeLabel: r.type,
    // NEX-1018 Review (fboudouhi Blocker 4): der Typ ist projekt-lokaler Klartext
    // (NEX-1016). Die Pill-Farben liegen unter den englischen Slugs;
    // requirementTypeSlug bildet die sieben Defaults ab und faellt sonst auf
    // "custom" zurueck — das Mapping oben verankert daraus die neutrale Pill.
    typeClass: `req-pill req-pill--type-${typeSlug}`,
    typeKind: TYPE_SLUG_TO_KIND[typeSlug] ?? 'custom',
    priorityLabel: translate.instant('requirements.priority.' + r.priority),
    priorityClass: pillClass('priority', r.priority),
    priorityLevel: PRIORITY_TO_LEVEL[r.priority] ?? 'mittel',
    statusLabel: statusLabel(r.status),
    // Colour by category, not the raw status: project-local statuses are
    // free-form (NEX-673). Der statusKind-Weg oben nutzt dieselbe Kategorie-
    // Zuordnung fuer die neue req-status-pill; statusClass bleibt fuer
    // Bestandstests bzw. den Fall stehen, dass eine Konsumseite noch das alte
    // CSS-Modell rendern muss (Aufraeumen tracket ein Follow-up-Ticket).
    statusClass: pillClass('status', statusCategory),
    statusKind: resolveStatusKind(r.status, statusOptions),
    statusCategory,
    author: r.authorUsername ?? null,
    tags: r.tags,
    reqId: r.id,
    rawType: r.type,
    rawPriority: r.priority,
    rawStatus: r.status,
  };
}

export function filterRows(
  rows: ReqRow[],
  term: string,
  tab: StatusTab,
  type: string | null,
  priority: string | null,
  author: string | null,
  statusValue: string | null = null,
): ReqRow[] {
  const normalized = term.toLowerCase().trim();
  return rows.filter((row) => {
    if (normalized) {
      const titleMatch = row.title.toLowerCase().includes(normalized);
      const idMatch = row.displayId.toLowerCase().includes(normalized);
      if (!titleMatch && !idMatch) return false;
    }
    if (tab === 'offen' && row.statusCategory !== 'open') return false;
    if (tab === 'review' && row.statusCategory !== 'review') return false;
    if (tab === 'fertig' && row.statusCategory !== 'done') return false;
    if (statusValue && row.rawStatus !== statusValue) return false;
    if (type && row.typeLabel !== type) return false;
    if (priority && row.priorityLabel !== priority) return false;
    if (author && row.author !== author) return false;
    return true;
  });
}

export function sortRows(rows: ReqRow[], sort: { col: TableSortCol | null; dir: 'asc' | 'desc' }): ReqRow[] {
  const { col, dir } = sort;
  if (!col) return rows;
  return [...rows].sort((a, b) => {
    let cmp: number;
    if (col === 'priorityLabel') {
      cmp = (PRIORITY_RANK[a.rawPriority ?? ''] ?? -1) - (PRIORITY_RANK[b.rawPriority ?? ''] ?? -1);
    } else {
      const av = (a[col] ?? '') as string;
      const bv = (b[col] ?? '') as string;
      cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
    }
    return dir === 'asc' ? cmp : -cmp;
  });
}

const EMPTY_SAVED_VIEW_FILTERS: RequirementSavedViewFilters = {
  query: null,
  statusFilter: null,
  statusValueFilter: null,
  typ: null,
  priority: null,
  author: null,
};

export function parseRequirementViewFilters(filtersJson: string | null): RequirementSavedViewFilters {
  return parseSavedViewFilters(filtersJson, EMPTY_SAVED_VIEW_FILTERS);
}

export function buildRequirementViewFiltersJson(filters: RequirementSavedViewFilters): string {
  return buildSavedViewFiltersJson(filters);
}

export function computeTabCounts(rows: ReqRow[]): TabCounts {
  const open = rows.filter((r) => r.statusCategory === 'open').length;
  const review = rows.filter((r) => r.statusCategory === 'review').length;
  const done = rows.filter((r) => r.statusCategory === 'done').length;
  return {
    total: rows.length,
    offen: open,
    review,
    fertig: done,
  };
}
